import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "./authSlice";
import { auth, storage, db } from "../../firebase/firebaseConfig";
import { updateProfile as firebaseUpdateProfile } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";

const EditProfileModal = ({ isOpen, onClose, initialDetails, onSave, showAvatar = true, disabledFields = [], isAgentEdit = false }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Responsive breakpoints
  const isMobile = useMediaQuery('(max-width:768px)');
  const isTablet = useMediaQuery('(max-width:1024px)');

  // Reusable TextField styling
  const textFieldStyles = {
    "& .MuiInputBase-root": {
      height: isMobile ? "44px" : "48px",
      borderRadius: "8px",
      borderWidth: "1px",
      fontSize: isMobile ? "14px" : "16px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#E0E0E0",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0063F7",
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      padding: isMobile ? "10px 14px" : "12px 16px",
      height: isMobile ? "22px" : "24px",
      display: "flex",
      alignItems: "center",
      textAlign: "left",
      color: "#212121",
      fontFamily: "Roboto",
      fontWeight: 400,
      fontSize: isMobile ? "14px" : "16px",
      "&::placeholder": {
        color: "#9E9E9E",
        opacity: 1,
        fontSize: isMobile ? "14px" : "16px",
        textAlign: "left",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: isMobile ? "14px" : "16px",
      color: "#757575",
      textAlign: "left",
      "&.Mui-focused": {
        fontSize: isMobile ? "14px" : "16px",
        color: "#0063F7",
      },
    },
  };

  const [editMode, setEditMode] = useState(false);

  // Reset edit mode when modal opens - always start in view mode
  useEffect(() => {
    if (isOpen) {
      setEditMode(false);
    }
  }, [isOpen]);
  const [formData, setFormData] = useState(() => ({
    email: initialDetails?.email || "",
    name: initialDetails?.name || "",
    phone: initialDetails?.phone || initialDetails?.mobile || "",
    location: initialDetails?.location || initialDetails?.address || "",
    ...(!isAgentEdit && { referredBy: initialDetails?.referredBy || "" }),
  }));

  // Update form data when initialDetails change or modal opens
  useEffect(() => {
    if (initialDetails && isOpen) {
      setFormData({
        email: initialDetails.email || "",
        name: !isAgentEdit ? (initialDetails.name || "").toUpperCase() : (initialDetails.name || ""),
        phone: initialDetails.phone || initialDetails.mobile || "",
        location: initialDetails.location || initialDetails.address || "",
        ...(!isAgentEdit && { referredBy: initialDetails.referredBy || "" }),
      });
    }
  }, [initialDetails, isOpen, isAgentEdit]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return ""; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validatePhone = (phone) => {
    if (!phone) return ""; // Phone is optional
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phone)) return "Please enter a valid 10-digit phone number";
    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.email = validateEmail(formData.email);

    // Skip phone validation if the field is disabled or it's an agent edit
    // This allows admin users with international numbers (e.g. +91) to save profile changes
    const phoneDisabled = disabledFields.includes("phone") || isAgentEdit;
    if (!phoneDisabled) {
      newErrors.phone = validatePhone(formData.phone);
    } else {
      newErrors.phone = "";
    }

    setErrors(newErrors);

    // Return true if no errors
    return !Object.values(newErrors).some(error => error !== "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply input restrictions based on field type
    if (name === 'phone') {
      // Only allow digits for mobile, limit to 10 characters
      processedValue = value.replace(/[^0-9]/g, '').substring(0, 10);
    }
    if (name === 'name' && !isAgentEdit) {
      processedValue = value.toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSave = async () => {
    try {
      // Validate form before saving
      if (!validateForm()) {
        toast.error("Please fix the validation errors before saving");
        return;
      }

      if (onSave) {
        // For agent editing, exclude email field; for client editing, include it
        const dataToSave = isAgentEdit
          ? (() => {
            // Create a clean object with only the fields we want for agents
            const cleanData = {
              name: formData.name,
              phone: formData.phone,
              location: formData.location,
              photoURL: formData.photoURL
            };
            // Filter out any undefined values to prevent Firebase errors
            const filteredData = Object.fromEntries(
              Object.entries(cleanData).filter(([_, value]) => value !== undefined)
            );
            return filteredData;
          })()
          : formData;
        // Call the parent's save handler with the updated form data
        await onSave(dataToSave);
        // Don't show toast here as the parent component will handle it
      } else {
        // Fallback for agent profile updates - save to Firestore
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Update displayName if name was changed
          if (formData.name && formData.name !== currentUser.displayName) {
            await firebaseUpdateProfile(currentUser, { displayName: formData.name });
          }

          // Update agent profile in Firestore
          const agentRef = doc(db, `agents/${currentUser.uid}`);
          const agentPayload = {
            name: formData.name || user.displayName || "",
            phone: formData.phone || user.phone || "",
            address: formData.location || user.address || "",
            email: currentUser.email || "",
            updatedAt: new Date().toISOString()
          };

          // Filter out any undefined values
          const cleanPayload = Object.fromEntries(
            Object.entries(agentPayload).filter(([_, value]) => value !== undefined)
          );

          await setDoc(agentRef, cleanPayload, { merge: true });

          // Update Redux store with new user data
          dispatch(setUser({
            ...user,
            displayName: formData.name || user.displayName,
            phone: formData.phone || user.phone,
            address: formData.location || user.address,
          }));
        }

        console.log("🎉 Agent profile update success - showing toast");
        toast.success("Account successfully updated", {
          containerId: "bottom-center",
          position: "bottom-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          closeButton: false,
          style: {
            backgroundColor: '#DCFCE7',
            color: '#FFFFFF',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            padding: '12px 16px',
            minHeight: '44px',
            alignItems: 'center',
            gap: '2px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            marginBottom: '75vh',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 'auto',
            maxWidth: 'none',
            display: 'inline-flex',
          },
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: '#FFFFFF' }}
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        });
      }
      setEditMode(false);
      onClose();
    } catch (err) {
      console.error("Error saving details:", err);
      toast.error("Failed to update account");
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    try {
      setUploading(true);
      const storageRef = ref(storage, `avatars/${user.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      await firebaseUpdateProfile(auth.currentUser, { photoURL });
      dispatch(setUser({ ...user, photoURL }));
      console.log("🎉 Profile picture update success - showing toast");
      toast.success("Profile picture successfully updated", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#166434',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    } catch (err) {
      console.error("Image upload failed", err);
      toast.error("Failed to update picture");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? false : isTablet ? "sm" : "md"}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : isTablet ? "500px" : "600px",
          maxWidth: isMobile ? "100%" : isTablet ? "90vw" : "600px",
          borderRadius: isMobile ? "0" : "12px",
          padding: isMobile ? "16px" : isTablet ? "24px" : "32px",
          margin: isMobile ? "0" : "32px",
          height: isMobile ? "100vh" : "auto",
          maxHeight: isMobile ? "100vh" : "90vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontFamily: "Roboto",
          fontWeight: "600",
          fontSize: isMobile ? "20px" : "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: isMobile ? "0 0 12px 0" : "0 0 16px 0",
        }}
      >
        {editMode ? "Edit Profile" : "View Profile"}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Avatar */}
      {showAvatar && (
        <Box display="flex" justifyContent="center" my={2} position="relative">
          <Avatar src={user?.photoURL} sx={{ width: 100, height: 100 }} />
          {editMode && (
            <label
              htmlFor="avatar-upload"
              style={{
                position: "absolute",
                bottom: 0,
                cursor: "pointer",
                background: "#fff",
                borderRadius: "50%",
                padding: "6px",
              }}
            >
              📷
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </label>
          )}
        </Box>
      )}

      {/* Form Fields */}
      <DialogContent sx={{ padding: isMobile ? "16px 0" : "24px 0" }}>
        <Box display="flex" flexDirection="column" gap={isMobile ? 2 : 3}>
          <TextField
            name="phone"
            label="Mobile"
            value={formData.phone}
            onChange={handleChange}
            disabled={!editMode || disabledFields.includes("phone") || (isAgentEdit && user?.isPhoneUser)}
            error={!!errors.phone}
            helperText={errors.phone}
            fullWidth
            sx={textFieldStyles}
          />
          <TextField
            name="name"
            label="Name"
            value={formData.name}
            onChange={handleChange}
            disabled={!editMode || disabledFields.includes("name")}
            fullWidth
            sx={textFieldStyles}
          />
          {(!isAgentEdit || formData.email) && (
            <>
              <TextField
                name="email"
                label="E-mail"
                value={formData.email}
                onChange={handleChange}
                disabled={!editMode || disabledFields.includes("email") || isAgentEdit}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                sx={{
                  ...textFieldStyles,
                  ...(isAgentEdit && {
                    "& .MuiInputBase-root": {
                      ...textFieldStyles["& .MuiInputBase-root"],
                      backgroundColor: "#F5F5F5",
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      ...textFieldStyles["& .MuiInputBase-input"],
                      color: "#757575",
                      cursor: "not-allowed",
                    },
                    "& .MuiInputLabel-root": {
                      ...textFieldStyles["& .MuiInputLabel-root"],
                      color: "#9E9E9E",
                    }
                  })
                }}
              />
              {isAgentEdit && (
                <Box sx={{
                  fontSize: "12px",
                  color: "#9E9E9E",
                  mt: -1,
                  mb: 1,
                  fontStyle: "italic"
                }}>
                  Email cannot be changed as it's used for login credentials
                </Box>
              )}
            </>
          )}
          <TextField
            name="location"
            label={(isAgentEdit && !user?.isPhoneUser) ? "Branch Name" : "Address"}
            value={formData.location}
            onChange={handleChange}
            disabled={!editMode || disabledFields.includes("location")}
            fullWidth
            sx={textFieldStyles}
          />
          {!isAgentEdit && (
            <TextField
              name="referredBy"
              label="Referred by"
              value={formData.referredBy}
              onChange={handleChange}
              disabled={!editMode || disabledFields.includes("referredBy")}
              fullWidth
              sx={textFieldStyles}
            />
          )}
        </Box>
      </DialogContent>

      {/* Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={isMobile ? 2 : 3} mt={isMobile ? 3 : 4} mb={0} px={0}>
        {!editMode ? (
          <>
            <Button
              variant="outlined"
              sx={{
                width: isMobile ? "100px" : "120px",
                height: isMobile ? "44px" : "48px",
                borderRadius: "8px",
                fontSize: isMobile ? "14px" : "16px",
                borderWidth: "1px",
                borderColor: "#B00020",
                color: "#B00020",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "inherit",
                  color: "#B00020",
                  boxShadow: 10
                }
              }}
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="contained"
              sx={{
                width: isMobile ? "100px" : "120px",
                height: isMobile ? "44px" : "48px",
                borderRadius: "8px",
                fontSize: isMobile ? "14px" : "16px",
                backgroundColor: "#0063F7 !important",
                color: "#FFFFFF",
                fontFamily: "Roboto",
                lineHeight: "24px",
                letterSpacing: "0.01em",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#0051d4 !important",
                },
              }}
              onClick={() => setEditMode(true)}
            >
              Edit
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              sx={{
                width: isMobile ? "100px" : "120px",
                height: isMobile ? "44px" : "48px",
                borderRadius: "8px",
                fontSize: isMobile ? "14px" : "16px",
                borderWidth: "1px",
                borderColor: "#B00020",
                color: "#B00020",
                fontFamily: "Roboto",
                lineHeight: "24px",
                letterSpacing: "0.01em",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "inherit",
                  color: "#B00020",
                  boxShadow: 10
                }
              }}
              onClick={() => setEditMode(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                width: isMobile ? "100px" : "120px",
                height: isMobile ? "44px" : "48px",
                borderRadius: "8px",
                fontSize: isMobile ? "14px" : "16px",
                backgroundColor: "#0063F7 !important",
                color: "#FFFFFF",
                fontFamily: "Roboto",
                lineHeight: "24px",
                letterSpacing: "0.01em",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#0051d4 !important",
                },
              }}
              onClick={handleSave}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Save"}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default EditProfileModal;